import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  Batch,
  BatchDepartureResp,
  BatchDepartureByIdRespWithSelected,
  BatchStatusEnum,
} from '../../models';
import {
  BatchesParseStorageService,
  BatchMailingIndexParams,
  DIRECTIONS_MAP,
  DOCUMENTS_MAP,
  NEGOTIATION_RATE_MAP,
  PAYMENT_TYPE_MAP,
  POSTAL_TYPE_MAP,
  RECIVING_MAP,
} from '$modules/personal/modules/batch';
import { BehaviorSubject } from 'rxjs';
import { User } from '$core/models/user';
import { PaginationParams } from '$shared/components/paginator/paginator.component';
import { Pagination } from '$core/models/pagination';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { ActivatedRoute, Router } from '@angular/router';
import { checkPhonePrefix } from '$shared/utils';

@Component({
  selector: 'app-view-batch-departures',
  templateUrl: './view-batch-departures.component.html',
  styleUrls: ['./view-batch-departures.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewBatchDeparturesComponent implements OnInit {
  private readonly batchStorageService = inject(BatchesParseStorageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  @Input() public activeTab: number;
  public batch = input<Batch>();
  @Input() public session$: BehaviorSubject<User>;
  @Input() public searchParam: string;
  @Input() public batchDeparturesLoading$ = new BehaviorSubject<boolean>(false);
  @Output()
  public batchDeparturesParamsChange: EventEmitter<BatchMailingIndexParams> =
    new EventEmitter();
  @Output() public sendDeletingDeparturesIds = new EventEmitter<number[]>();

  public readonly PAYMENT_TYPE_MAP = PAYMENT_TYPE_MAP;
  public readonly RECIVING_MAP = RECIVING_MAP;
  public readonly DIRECTIONS_MAP = DIRECTIONS_MAP;
  public readonly DOCUMENTS_MAP = DOCUMENTS_MAP;
  public readonly NEGOTIATION_RATE_MAP = NEGOTIATION_RATE_MAP;
  public readonly POSTAL_TYPE_MAP = POSTAL_TYPE_MAP;
  public readonly checkPhonePrefix = checkPhonePrefix;
  public readonly faTrashAlt = faTrashAlt;
  public readonly BatchStatusEnum = BatchStatusEnum;

  protected readonly batchDepartures = this.batchStorageService.batchDepartures;

  public get isAllDeparturesSelected() {
    return this.batchDepartures().data.every((departure) => departure.selected);
  }

  public get selectedDepartures(): BatchDepartureByIdRespWithSelected[] {
    return this.batchDepartures().data.filter(
      (departure) => departure.selected
    );
  }

  public ngOnInit() {}

  public selectAllCards() {
    const initialDepartures = this.batchDepartures();

    const updatedDepartures = initialDepartures.data.map((batchItem) => ({
      ...batchItem,
      selected: !this.isAllDeparturesSelected,
    }));

    this.batchDepartures.set({
      ...initialDepartures,
      data: updatedDepartures as BatchDepartureByIdRespWithSelected[],
    });
  }

  public searchBatchDepartures(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    setTimeout(() => {
      this.batchDeparturesParamsChange.emit({
        batchId: this.batch().id,
        search: inputElement.value,
      });
    }, 700);
  }

  public pageChanged(paginationEvent: PaginationParams) {
    this.batchDeparturesParamsChange.emit({
      batchId: this.batch().id,
      ...paginationEvent,
    });
  }

  public convertInputData(convertingData: BatchDepartureResp): Pagination {
    return {
      current_page: convertingData.meta.current_page,
      first_page_url: convertingData.links.first,
      last_page: convertingData.meta.last_page,
      last_page_url: convertingData.links.last,
      path: convertingData.meta.path,
      per_page: convertingData.meta.per_page,
      to: convertingData.meta.to,
      total: convertingData.meta.total,
      data: convertingData.data,
      from: convertingData.meta.from,
    };
  }

  public selectSingleDeparture(params: { id: number; selected: boolean }) {
    const batchDepartures = this.batchDepartures();

    const updatedDepartures = batchDepartures.data.map((batchItem) =>
      batchItem.id === params.id
        ? { ...batchItem, selected: params.selected }
        : batchItem
    );

    this.batchDepartures.set({
      ...batchDepartures,
      data: updatedDepartures as BatchDepartureByIdRespWithSelected[],
    });
  }

  public deleteSelectedDepartures() {
    const deletedDeparturesIds = this.batchDepartures()
      .data.filter((departure) => departure.selected)
      .map((item) => item.id);

    this.sendDeletingDeparturesIds.emit(deletedDeparturesIds);
  }

  public navigateToCreateDeparturePage() {
    const id = this.activatedRoute.snapshot.params['id'];
    return this.router.navigateByUrl(`personal/batch/batches/${id}/create`);
  }
}
